import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import theme from 'admin/theme/muiTheme';

import { requirementForPath } from 'constants/navPermissions';
import { firstVisiblePath } from 'helpers/navVisibility';
import navigationConfig from 'admin/config/navigation';
import { useAuth } from 'admin/hooks/useAuth';
import { describeModuleKey } from 'constants/moduleLabels';
import { colors, palette, typography } from 'theme/tokens';

/**
 * 403 for the platform admin portal.
 *
 * Shown when a signed-in operator reaches a screen their role does not grant —
 * by typing the URL, by following a stale bookmark, or by a link from a screen
 * that has not learned to hide it yet.
 *
 * ── Why it names the permission ─────────────────────────────────────────────
 *
 * The remedy for a 403 here is always the same: somebody with Role Management
 * ticks a box. That person needs to know WHICH box, and the operator hitting the
 * wall is the one who will ask them. Printing the module string turns a support
 * round trip into a copy-paste. It leaks nothing — the catalogue is visible to
 * anyone who can open the role editor, and the string is already in the bundle.
 *
 * ── Why "sign in again" ─────────────────────────────────────────────────────
 *
 * Permissions are snapshotted into the session when it is minted and refreshed
 * only when the access token rotates. A grant made a minute ago is not visible to
 * a tab that has been open since before it. Saying so up front is cheaper than
 * the alternative, which is the operator being told "I gave you that" and both of
 * them believing the portal is broken.
 */
export default function AccessDeniedPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { perms } = useAuth();

  /**
   * Somewhere this role can actually go — NOT a hardcoded dashboard.
   *
   * The Dashboard is a gated module like any other, so for a role that lacks it
   * a "Back to Dashboard" button is a button back to this same screen. When
   * nothing at all is visible the button is dropped rather than made a loop.
   */
  const home = firstVisiblePath(navigationConfig, perms);

  const requirement = requirementForPath(pathname);
  const options = Array.isArray(requirement) ? requirement : requirement ? [requirement] : [];
  // Named the way the role editor names them, so the operator asks for a thing
  // the administrator can actually find. See constants/moduleLabels.js.
  const codes = options
    .filter((option) => option && option.side)
    .map(({ side, module, subModule }) =>
      describeModuleKey(subModule ? `${side}:${module}/${subModule}` : `${side}:${module}`),
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3,
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: typography.fontSize[64], color: colors.accent.iconSoft, mb: 2 }} />
      <Typography variant="h4" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: typography.fontWeight.bold }}>
        You do not have access to this page
      </Typography>
      <Typography sx={{ color: palette.neutral[470], mb: codes.length ? 2 : 3, maxWidth: 560 }}>
        Your role does not include this screen. Ask an administrator to grant it, then sign out and back in — a new
        permission does not reach a session that is already open.
      </Typography>

      {codes.length > 0 && (
        <Typography
          component="div"
          sx={{
            color: palette.neutral[580],
            mb: 3,
            fontSize: typography.fontSize[13],
            fontFamily: typography.fontFamily.monoSystem,
            backgroundColor: colors.accent.surface,
            border: `1px solid ${colors.accent.surfaceBorder}`,
            borderRadius: '8px',
            px: 2,
            py: 1.25,
          }}
        >
          {/* Plural only when it genuinely is: a two-tab screen opens for either. */}
          {codes.length > 1 ? 'Requires any of: ' : 'Requires: '}
          {codes.join('  ·  ')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {home && (
          <Button variant="contained" color="primary" onClick={() => navigate(home)}>
            {home === '/admin' ? 'Back to Dashboard' : 'Go to a page you can open'}
          </Button>
        )}
        {/* -1 rather than a fixed route: the operator arrived from somewhere, and
            that somewhere is a screen they could actually open. */}
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    </Box>
  );
}
